import { useContext, useState, useEffect } from 'react'
import { useWeb3React } from '@web3-react/core'
import { BigNumber } from 'ethers'
import getConfig from 'next/config'

import { StoreContext, StoreAction } from 'web/lib/store'
import usePolygonBlockNumber from 'web/hooks/usePolygonBlockNumber'
import usePoller from 'web/hooks/usePoller'
import useRebaserData from 'web/hooks/useRebaserData'

import CultStakingDialog from 'web/components/CultStakingDialog'
import PowerRing from 'web/components/PowerRing'
import 'web/lib/string-utils'

import classNames from 'classnames'
import PowerIcon from './PowerIcon'
import { InformationCircleIcon } from '@heroicons/react/outline'

import Tooltip from './Tooltip'
import tooltipStyles from './Tooltip.module.css'
import useBoundCult from 'web/hooks/useBoundCult'
import useMyBoons from 'web/hooks/useMyBoons'
import { CacheKey } from 'web/lib/cache-keys'
import { useSWRConfig } from 'swr'
import usePolygonBlockTime from 'web/hooks/usePolygonBlockTime'
import useMyGawds from 'web/hooks/useMyGawds'
import { isPolygonMainnet, polygonNetworkName } from 'web/lib/contract'
import Bubble from './Bubble'
import { EthLink } from './EthLink'
import { PowerType } from 'web/lib/gawd'

const {
  publicRuntimeConfig: { discordInviteUrl },
} = getConfig()

const CultGame = () => {
  const { account, chainId } = useWeb3React()
  const { mutate } = useSWRConfig()

  const {
    cultGame: [cultGame, dispatchCult],
    gawdtchis: [gawdtchis]
  } = useContext(StoreContext)

  const polygonBlock = usePolygonBlockNumber(true)
  const secondsPerBlock = usePolygonBlockTime()

  const myBoons = useMyBoons()

  const [cultSelectorVisible, setCultSelectorVisible] = useState<boolean>(false)

  // load all the rebaser data
  const { cultStats, currentCultBoost, error: rebaseError } = useRebaserData()

  // time till next rebase
  const [nextRebaseTime, setNextRebaseTime] = useState<number>(1)

  // if next rebase block updates, update our time estimate
  // don't trigger on polygonBlock changes - makes clock behave oddly
  useEffect(() => {
    if (!cultGame?.nextRebaseBlock) return
    if (!polygonBlock) return

    const seconds = (BigNumber.from(cultGame.nextRebaseBlock).sub(polygonBlock).toNumber()) * secondsPerBlock
    setNextRebaseTime(seconds)
  }, [cultGame?.nextRebaseBlock, polygonBlock])

  // update timer every 1s
  const interval = usePoller(1000)
  useEffect(() => {
    setNextRebaseTime(nextRebaseTime - 1)

    // If time ran out, bust the cache and only attempt this once every 60 seconds
    if (cultGame.nextRebaseBlock && nextRebaseTime <= 0 && nextRebaseTime % (60 * 10) == 0) {
      console.log("Time ran out. Refreshing data...", nextRebaseTime)
      mutate(CacheKey.rebaserData(account))

      // Clear polygon block cache
      mutate(CacheKey.polygonBlock)
    }
  }, [interval])

  const myGawds = useMyGawds()

  // Filter preloaded gawchis down to the ones that we own
  let myGawdIds: number[] = myGawds.map(gawd => gawd.id())
  const myGawchis = gawdtchis.gawds.filter(g => myGawdIds.includes(g.gawdId()))

  const currentCult = useBoundCult()

  const [isBinding, setIsBinding] = useState<boolean>(false)

  function onBinding(isBinding: boolean) {
    setIsBinding(isBinding)
  }

  // calculate displayed yields
  const [nextRewardAmount, setNextRewardAmount] = useState<number>(0)
  const [satedGawdEarnings, setSatedGawdEarnings] = useState<number>(0)
  const [baseEarnings, setBaseEarnings] = useState<number>(0)
  const [cultBoostEarnings, setCultBoostEarnings] = useState<number>(0)
  const [earlyAccessEarnings, setEarlyAccessEarnings] = useState<number>(0)

  useEffect(() => {
    const satedEarnings = cultGame.baseEnergyPerGawd * cultGame.happyGawds * cultGame.globalGawdHealth
    const base = satedEarnings + cultGame.alignedPowerEarnings

    setSatedGawdEarnings(satedEarnings)
    setBaseEarnings(base)
    setCultBoostEarnings(base * currentCultBoost)
    setEarlyAccessEarnings(base * cultGame.earlyAccessBoost)

    setNextRewardAmount(
      base * (cultGame.earlyAccessBoost + currentCultBoost) + base
    )
  }, [cultGame.happyGawds, currentCult?.id, account, cultGame, myGawchis, cultGame.globalGawdHealth])

  const [powerHover, setPowerHover] = useState<PowerType>(null)

  return (
    <div className="space-y-8 md:space-y-8">
      <div>
        <h1 className="text-3xl md:text-4xl text-center">My Sanctuary</h1>
        <p className="text-center">
          <EthLink chainId={chainId} type='account' hash={account} className='text-sm' />
        </p>
      </div>

      <div className="lg:grid lg:grid-cols-3 lg:gap-4">
        {/* COLUMN 1 */}
        <div className={`space-y-6 md:flex md:flex-col md:justify-center`}>
          <div className="space-y-2">
            <div className="flex flex-row space-x-4">
              <h5>Blood Game</h5>
              <div>
                <Bubble>
                  {polygonNetworkName}
                </Bubble>
              </div>
            </div>
            <p className="text-gray-500">
              Every 1/6 Earth cycle, your Gawds will reward you with $BLOOD - but only if they are sated.
              Care for each Gawd daily to ensure a Blessed Yield.
            </p>
            {currentCult && !isPolygonMainnet && <p className="text-gray-500">
              This is a Testnet. Blood earnings will not rollover to Mainnet launch
            </p>}
            {!currentCult && (
              <p className="text-gray-500">
                To begin, <a href="/wallet" className="hover:text-red-500 transition-all" onClick={(ev) => {
                  ev.preventDefault()
                  setCultSelectorVisible(true)
                }}>bind your Gawds</a> to start producing $BLOOD.
              </p>
            )}
            <p>
              <a href="https://gawds.notion.site/Blood-Token-dee95904528144bd8552c02506731f30" target="_blank" className="text-gray-500 hover:text-red-500 transition-all">Learn more</a>
            </p>
          </div>

          <div className="flex-1 grid grid-cols-2 gap-x-4 gap-y-4 md:gap-y-0 pb-4 md:pb-4">

            <Tooltip
              className="space-y-1"
              tooltipHtml={<>For each of your sated Gawds, you<br /> will earn up to 1 $BLOOD every 4 hours.<br /> Earnings are function of your Gawds'<br /> overall health.</>}
            >
              <div className="text-gray-500">Max Earnings per Gawd</div>
              <div className="text-2xl font-bold text-white">
                1.00 <span className="text-sm">$BLOOD</span>
              </div>
            </Tooltip>

            <div className="space-y-1">
              <div className="text-gray-500">Overall Health</div>
              <div className="font-bold text-white text-2xl">
                {cultGame.globalGawdHealth == 0 ?
                  'DEAD'
                  :
                  <>
                    {(cultGame.globalGawdHealth * 100).toFixed(1)}%
                  </>
                }
              </div>
            </div>

            <div className="space-y-1">
              <div className="text-gray-500">Current Power</div>
              <div className="font-bold text-white text-2xl">
                <PowerIcon power={cultGame.alignedPower} className="w-5 h-5 inline fill-white" /> {cultGame.alignedPower?.name || '...'}
              </div>
            </div>

            {currentCult && myBoons.active.length > 0 &&
              <div className="space-y-1">
                <div className="text-gray-500">Active Boons</div>
                <div className="font-bold text-white text-2xl flex flex-row">
                  {myBoons.active.map(boon => {
                    return (
                      <div key={boon.uid}>
                        <Tooltip
                          className="space-y-1"
                          tooltipHtml={<>{boon.name} expires in {boon.getTimeRemaining(polygonBlock, secondsPerBlock).timeForHumans()}</>}
                          place={'top'}
                        >
                          <img src={boon.imageUrl} className="w-10 h-10 inline" />
                        </Tooltip>
                      </div>
                    )
                  })}
                </div>
              </div>
            }
          </div>
        </div>

        {/* COLUMN 2 */}
        <div className="relative text-center aspect-square">
          <PowerRing selectedPower={cultGame.alignedPower}
            isBinding={isBinding}
            className="absolute inset-0 z-[2]"
            onMouseOver={(p) => setPowerHover(p)}
            onMouseOut={() => setPowerHover(null)} />

          <div className=" flex flex-col justify-center items-center aspect-square">

            {/* NEWER  */}
            {(currentCult != null && !isBinding && (
              <>
                <div className="align-center ">
                  <div className="inline-block text-center">
                    {powerHover ?
                      <PowerIcon power={powerHover} className="w-14 h-14 fill-red-500" />
                      :
                      <img src={`/images/${currentCult.name.toLowerCase()}-icon-red.svg`} alt="Arcane" className="w-14 h-14" />
                    }
                  </div>
                  <div className="text-red-500 relative z-[10]">
                    {powerHover ?
                      <span className="text-sm">{powerHover}</span>
                      :
                      <a className="text-sm" href="/wallet" onClick={(ev) => {
                        ev.preventDefault()
                        setCultSelectorVisible(true)
                      }}>
                        {currentCult.fullName}
                      </a>}
                  </div>
                </div>

                {/* Rebase countdown  */}
                <div className="flex items-center gap-2 p-6">
                  <div className="text-red-500 text-sm">Est. Next<br /> Blessing</div>
                  {nextRebaseTime.toHours() > 0 &&
                    <div className="text-white bg-red-500 p-0 px-3 rounded-xl">
                      <h2 className="text-2xl pr-1 inline-block pb-2 pt-0 mr-1 w-7">{nextRebaseTime.toHours() < 0 ? 0 : nextRebaseTime.toHours()}</h2>
                      hrs
                    </div>
                  }
                  <div className="text-white bg-red-500 p-0 px-3 rounded-xl">
                    <h2 className="text-2xl pr-1 inline-block pb-2 pt-0 mr-1 w-7">{nextRebaseTime.toMinutes() < 0 ? 0 : nextRebaseTime.toMinutes()}</h2>
                    min
                  </div>
                  {nextRebaseTime.toHours() <= 0 &&
                    <div className="text-white bg-red-500 p-0 px-3 rounded-xl">
                      <h2 className="text-2xl pr-1 inline-block pb-2 pt-0 w-6">{nextRebaseTime.toSeconds() < 0 ? 0 : nextRebaseTime.toSeconds()}</h2>&nbsp;sec
                    </div>
                  }
                </div>

                {/* Earnings estimate  */}
                {rebaseError || nextRebaseTime < -120 ?
                  <div className="p-4 text-sm text-gray-500">
                    Rewards are on the fritz.<br />
                    Check <a href={discordInviteUrl} target="_blank">Discord</a> for updates.
                  </div>
                  :
                  <div className="p-4 text-sm text-gray-500">
                    Your est. earnings
                    <div className="text-2xl text-red-500 font-bold">
                      {(nextRewardAmount >= 0 && nextRewardAmount.toPrecision(3) + ' $BLOOD') || 0}
                    </div>
                  </div>}
              </>
            ))}

            {currentCult == null && !isBinding && (
              // Bind cult
              <button
                className="font-bold no-underline text-white bg-red-500 hover:bg-red-300 rounded-lg p-4 md:p-3 relative z-[10]"
                onClick={(ev) => setCultSelectorVisible(true)}>
                Bind My Gawds
              </button>
            )}

            {currentCult == null && isBinding && (
              <div className="text-red-500 text-xl font-bold">Binding...</div>
            )}

            {currentCult != null && isBinding && (
              <div className="text-red-500 text-xl font-bold">Unbinding...</div>
            )}
          </div>
        </div>

        {/* COLUMN 3 */}
        <div className={classNames(`flex flex-col space-y-2 mt-8 md:mt-0`,
          currentCult?.id > 0 ? 'visible' : 'opacity-30')}>

          <h5>Blessing Calculator</h5>
          <div className="flex flex-col text-gray-500 space-y-6">
            {/* Sated Gawds */}
            <div className="">
              <div className="pb-2 flex flex-row">
                Your Sated Gawds
                <Tooltip place="top" tooltipClassName="flex flex-col max-w-xs" tooltipHtml={
                  <div>
                    Keep your Gawds sated (AKA alive) by visiting each of your Gawds
                    and using the TamaGawdtchi actions.<br />
                    <span className={tooltipStyles.math}>
                      {cultGame.happyGawds} Sated Gawds
                      &times; {(cultGame.globalGawdHealth * 100).toFixed(1)}% Health
                      = {satedGawdEarnings.toFixed(2)} $BLOOD
                    </span>
                  </div>
                }>
                  <InformationCircleIcon className="inline w-4 ml-1" />
                </Tooltip>
              </div>
              <div className="flex flex-row w-4/5">
                <div className="font-bold text-white text-2xl flex-grow">
                  {cultGame.happyGawds === null ? '0' : cultGame.happyGawds}<span className="text-gray-500">/{myGawds.length}</span>
                </div>
                <div className={classNames("pt-1 font-bold flex-none", cultGame.happyGawds > 0 ? "text-red-500" : "text-gray-700")}>
                  + {satedGawdEarnings.toFixed(2)} $BLOOD
                </div>
              </div>
            </div>

            {/* Power-aligned Gawds  */}
            <div className="">
              <div className="pb-2 flex flex-row">
                Your {cultGame.alignedPower?.name || 'Aligned'} Gawds
                <Tooltip place="top" tooltipClassName="flex flex-col max-w-xs" tooltipHtml={
                  <div>
                    You own {cultGame.alignedGawdsByPowerCount} Gawd{cultGame.alignedGawdsByPowerCount == 1 ? '' : 's'} with
                    the <span className={tooltipStyles.math}>{cultGame.alignedPower?.name || 'aligned'}</span> dominant power.
                    Every blessing a new power is selected.<br />
                    <span className={tooltipStyles.math}>
                      {cultGame.alignedGawdsByPowerCount} {cultGame.alignedPower?.name || 'aligned'} Gawds
                      &times; {(cultGame.globalGawdHealth * 100).toFixed(1)}% Health
                      &times; {(cultGame.alignedPowerBoost * 100).toFixed(2)}% Bonus
                      = {cultGame.alignedPowerEarnings.toFixed(2)} $BLOOD
                    </span>
                  </div>
                }>
                  <InformationCircleIcon className="inline w-4 ml-1" />
                </Tooltip>
              </div>
              <div className="flex flex-row w-4/5">
                <div className="font-bold text-white flex-auto text-2xl">
                  <PowerIcon power={cultGame.alignedPower} className="w-5 h-5 inline fill-white" /> {cultGame.alignedGawdsByPowerCount}
                </div>
                <div className={classNames("pt-1 font-bold flex-none", cultGame.alignedGawdsByPowerCount > 0 ? "text-red-500" : "text-gray-700")}>
                  + {cultGame.alignedPowerEarnings.toFixed(2)} $BLOOD
                </div>
              </div>
            </div>

            {/* Cult Boost  */}
            <div className="">
              <div className="pb-2 flex flex-row">
                {currentCult?.fullName || 'Cult'} Boost
                <Tooltip place="top" tooltipClassName="flex flex-col max-w-xs" tooltipHtml={
                  <div>
                    Your cult receives an extra {(currentCultBoost * 100).toFixed(2)}% boost.<br />
                    <span className={tooltipStyles.math}>
                      {(satedGawdEarnings + cultGame.alignedPowerEarnings).toFixed(2)} $BLOOD
                      &times; {(currentCultBoost * 100).toFixed(2)}% = {
                        (currentCultBoost * (satedGawdEarnings + cultGame.alignedPowerEarnings)).toFixed(2)
                      } $BLOOD
                    </span>
                  </div>
                }>
                  <InformationCircleIcon className="inline w-4 ml-1" />
                </Tooltip>
              </div>
              <div className="flex flex-row w-4/5">
                <div className="font-bold text-white text-2xl flex-auto">
                  + {(currentCultBoost * 100).toFixed(1)}%
                </div>
                <div className={classNames("pt-1 font-bold flex-none", cultBoostEarnings > 0 ? "text-red-500" : "text-gray-700")}>
                  + {cultBoostEarnings.toFixed(2)} $BLOOD
                </div>
              </div>
            </div>

            {/* Early Eccess Boost  */}
            <div className="">
              <div className="pb-2 flex flex-row">
                Early Access Boost
                <Tooltip place="top" tooltipClassName="flex flex-col max-w-xs" tooltipHtml={
                  <div>
                    We reward early participation with a {(cultGame?.earlyAccessBoost * 100).toFixed(0)}% boost. After every blessing cycle, the boost decreases by 1%<br />
                    <span className="bg-gray-900 px-1 font-mono rounded-lg">
                      {(baseEarnings).toFixed(2)} $BLOOD
                      × {(cultGame?.earlyAccessBoost * 100).toFixed(0)}% = {earlyAccessEarnings.toFixed(2)} $BLOOD
                    </span>
                  </div>
                }>
                  <InformationCircleIcon className="inline w-4 ml-1" />
                </Tooltip>
              </div>
              <div className="flex flex-row w-4/5">
                <div className="font-bold text-white text-2xl flex-auto">
                  + {(cultGame.earlyAccessBoost * 100).toFixed(0)}%
                </div>
                <div className={classNames("pt-1 font-bold flex-none", earlyAccessEarnings > 0 ? "text-red-500" : "text-gray-700")}>
                  + {earlyAccessEarnings.toFixed(2)} $BLOOD
                </div>
              </div>
            </div>

          </div>

        </div>
      </div>

      <CultStakingDialog isOpen={cultSelectorVisible} setIsOpen={setCultSelectorVisible} onBinding={onBinding} />
    </div>
  )
}

export default CultGame
